import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { NgIf } from '@angular/common';
import type { PhTreeItem } from '@payhoa/ui-kit/elements';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { OrganizationFile } from '../../../../models/entities/organization-file';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { DocumentsService } from '../../../../services/communication/documents.service';

export const FOLDER_PICKER_ROOT_ID = -1;
export const FOLDER_PICKER_ROOT_NAME = 'Documents';

export interface FolderPickerSelection {
    id: number;
    path: string[];
}

interface FolderNode {
    id: number;
    name: string;
    fileName: string;
    directory: boolean;
    parentId?: number | null;
    children: FolderNode[];
}

/** Org document folders as a searchable tree under a synthetic "Documents" root (id -1); emits the chosen folder and its path. */
@UntilDestroy()
@Component({
    selector: 'app-folder-picker',
    templateUrl: './folder-picker.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FolderPickerComponent implements OnInit, OnChanges {
    @Input() organizationId?: number;
    @Input() selectedFolderId: number | null = null;
    @Input() showPathPreview = true;

    @Output() readonly folderSelected = new EventEmitter<FolderPickerSelection>();
    @Output() readonly loaded = new EventEmitter<OrganizationFile[]>();
    @Output() readonly loadFailed = new EventEmitter<void>();

    readonly rootId = FOLDER_PICKER_ROOT_ID;

    isLoading = true;
    hasFailed = false;
    nodes: FolderNode[] = [];
    items: PhTreeItem[] = [];
    expandedIds: string[] = [String(FOLDER_PICKER_ROOT_ID)];
    activeId: string | null = null;
    searchQuery = '';
    selectedPath: string[] = [];

    private _selectedId: number | null = null;
    private _byId = new Map<number, FolderNode>();

    constructor(
        private readonly _documents: DocumentsService,
        private readonly _cdr: ChangeDetectorRef,
    ) {}

    get pathDisplay(): string {
        return this.selectedPath.join(' / ');
    }

    get hasSelection(): boolean {
        return this.selectedPath.length > 0;
    }

    ngOnInit(): void {
        this._selectedId = this.selectedFolderId;
        this.load();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedFolderId'] && !changes['selectedFolderId'].firstChange) {
            this._selectedId = this.selectedFolderId;
            this._applySelection();
        }
    }

    load(): void {
        this.isLoading = true;
        this.hasFailed = false;
        this._cdr.markForCheck();
        const orgId = this.organizationId ?? LegFiJwtService.read()?.orgId;
        this._documents.getFlatDocumentsForOrganization(orgId).pipe(untilDestroyed(this)).subscribe({
            next: (res: Object) => {
                const files = ((res as Object[]) ?? []).map((doc) => new OrganizationFile(doc));
                this.nodes = [{
                    id: FOLDER_PICKER_ROOT_ID,
                    directory: true,
                    fileName: FOLDER_PICKER_ROOT_NAME,
                    name: FOLDER_PICKER_ROOT_NAME,
                    children: this._createTree(files.filter((f) => f.directory)),
                }];
                this._byId = new Map();
                this._index(this.nodes, null);
                this.items = this.nodes.map((node) => this._toItem(node));
                this.expandedIds = [String(FOLDER_PICKER_ROOT_ID)];
                this.isLoading = false;
                this._applySelection();
                this._cdr.markForCheck();
                this.loaded.emit(files);
            },
            error: () => {
                this.isLoading = false;
                this.hasFailed = true;
                this._cdr.markForCheck();
                this.loadFailed.emit();
            },
        });
    }

    /** A row click or Enter on a folder picks it. */
    onSelect(id: string): void {
        const folderId = Number(id);
        if (!this._byId.has(folderId)) {
            return;
        }
        this._select(folderId);
    }

    onSearchInput(event: Event): void {
        this.onSearchChange((event.composedPath()[0] as HTMLInputElement)?.value ?? '');
    }

    onSearchChange(query: string): void {
        this.searchQuery = query.toLowerCase().trim();
        if (!this.searchQuery) {
            this.expandedIds = [String(FOLDER_PICKER_ROOT_ID)];
        }
        this._cdr.markForCheck();
    }

    clearSearch(): void {
        this.onSearchChange('');
    }

    private _applySelection(): void {
        if (this._selectedId === null || !this._byId.has(this._selectedId)) {
            return;
        }
        const ancestors = this._ancestors(this._selectedId).map(String);
        this.expandedIds = [...new Set([...this.expandedIds, ...ancestors])];
        this._select(this._selectedId);
    }

    private _select(id: number): void {
        this._selectedId = id;
        this.activeId = String(id);
        this.selectedPath = this._buildPath(id);
        this._cdr.markForCheck();
        this.folderSelected.emit({ id, path: [...this.selectedPath] });
    }

    private _buildPath(id: number): string[] {
        const path: string[] = [];
        let current = this._byId.get(id);
        while (current) {
            path.unshift(current.fileName || current.name);
            current = current.parentId === null || current.parentId === undefined ? undefined : this._byId.get(current.parentId);
        }
        return path;
    }

    private _ancestors(id: number): number[] {
        let ids: number[] = [];
        let parentId = this._byId.get(id)?.parentId;
        while (parentId !== null && parentId !== undefined && this._byId.has(parentId)) {
            ids = [...ids, parentId];
            parentId = this._byId.get(parentId)?.parentId;
        }
        return ids;
    }

    private _index(nodes: FolderNode[], parentId: number | null): void {
        nodes.forEach((node) => {
            node.parentId = parentId;
            this._byId.set(node.id, node);
            this._index(node.children, node.id);
        });
    }

    private _toItem(node: FolderNode): PhTreeItem {
        return { id: String(node.id), label: node.fileName, folder: true, children: node.children.map((child) => this._toItem(child)) };
    }

    private _createTree(directories: OrganizationFile[]): FolderNode[] {
        const byId: Record<number, FolderNode> = Object.create(null);
        directories.forEach((item) => {
            byId[item.id] = { id: item.id, fileName: item.fileName, name: item.fileName, directory: true, parentId: item.parentId, children: [] };
        });
        directories.forEach((item) => {
            if (item.parentId && byId[item.parentId]) {
                byId[item.parentId].children = [...byId[item.parentId].children, byId[item.id]];
            }
        });
        return directories.filter((item) => !item.parentId).map((item) => byId[item.id]);
    }
}
